import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import joiningTemplate from "../templates/joiningRequirementsTemplate.xlsx";

const setCell = (sheet, cell, value = "") => {
  sheet.getCell(cell).value = value ?? "";
};

const copyRowStyle = (sheet, fromRowNumber, toRowNumber) => {
  const fromRow = sheet.getRow(fromRowNumber);
  const toRow = sheet.getRow(toRowNumber);

  toRow.height = fromRow.height;

  fromRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const newCell = toRow.getCell(colNumber);

    newCell.style = JSON.parse(JSON.stringify(cell.style || {}));
    newCell.numFmt = cell.numFmt;
    newCell.alignment = cell.alignment;
    newCell.border = cell.border;
    newCell.fill = cell.fill;
    newCell.font = cell.font;
  });
};

const formatPeriod = (period) => {
  if (!period?.from && !period?.to) return "";
  return `${period?.from || ""} - ${period?.to || ""}`;
};

const getDayTime = (workTime = [], dayKey) => {
  if (!Array.isArray(workTime) || workTime.length === 0) {
    return { from: "", to: "" };
  }

  const dayData = workTime[0]?.days?.[dayKey];

  if (!dayData) {
    return { from: "", to: "" };
  }

  const morning = formatPeriod(dayData.morning);
  const evening = formatPeriod(dayData.evening);

  if (morning && evening) {
    return {
      from: morning,
      to: evening,
    };
  }

  if (morning) {
    return {
      from: dayData.morning?.from || "",
      to: dayData.morning?.to || "",
    };
  }

  if (evening) {
    return {
      from: dayData.evening?.from || "",
      to: dayData.evening?.to || "",
    };
  }

  return { from: "", to: "" };
};

const WORK_TIME_TEMPLATE_START = 11;
const WORK_TIME_TEMPLATE_END = 26;
const WORK_TIME_TEMPLATE_HEIGHT =
  WORK_TIME_TEMPLATE_END - WORK_TIME_TEMPLATE_START + 1;

const WORK_TIME_DAYS = [
  { key: "sunday", labelRowOffset: 2 },
  { key: "monday", labelRowOffset: 4 },
  { key: "tuesday", labelRowOffset: 6 },
  { key: "wednesday", labelRowOffset: 8 },
  { key: "thursday", labelRowOffset: 10 },
  { key: "friday", labelRowOffset: 12 },
  { key: "saturday", labelRowOffset: 14 },
];

const copyCellStyle = (fromCell, toCell) => {
  toCell.style = JSON.parse(JSON.stringify(fromCell.style || {}));
  toCell.numFmt = fromCell.numFmt;
  toCell.alignment = fromCell.alignment;
  toCell.border = fromCell.border;
  toCell.fill = fromCell.fill;
  toCell.font = fromCell.font;
};

const safeMergeCells = (sheet, range) => {
  try {
    sheet.mergeCells(range);
  } catch (error) {
    // الخلية ممكن تكون مدموجة مسبقًا في التمبلت
  }
};

const mergeWorkTimeBlockCells = (sheet, startRow) => {
  safeMergeCells(sheet, `D${startRow}:F${startRow}`);

  WORK_TIME_DAYS.forEach((day) => {
    const row = startRow + day.labelRowOffset;
    safeMergeCells(sheet, `D${row}:D${row + 1}`);
  });
};

const copyWorkTimeBlockStyle = (sheet, startRow) => {
  for (let rowOffset = 0; rowOffset < WORK_TIME_TEMPLATE_HEIGHT; rowOffset++) {
    const templateRowNumber = WORK_TIME_TEMPLATE_START + rowOffset;
    const targetRowNumber = startRow + rowOffset;

    sheet.getRow(targetRowNumber).height = sheet.getRow(templateRowNumber).height;

    ["D", "E", "F"].forEach((column) => {
      const sourceCell = sheet.getCell(`${column}${templateRowNumber}`);
      const targetCell = sheet.getCell(`${column}${targetRowNumber}`);

      copyCellStyle(sourceCell, targetCell);
      targetCell.value = sourceCell.value || "";
    });
  }
};

const setWorkTimeBlock = (sheet, branch, startRow, index) => {
  if (startRow !== WORK_TIME_TEMPLATE_START) {
    copyWorkTimeBlockStyle(sheet, startRow);
  }

  mergeWorkTimeBlockCells(sheet, startRow);

  const branchName = branch?.name || `Branch ${index + 1}`;

  setCell(sheet, `D${startRow}`, `اوقات العمل - ${branchName}`);

  WORK_TIME_DAYS.forEach((day) => {
    const row = startRow + day.labelRowOffset;
    const time = getDayTime(branch?.workTime || [], day.key);

    setCell(sheet, `E${row}`, time.from);
    setCell(sheet, `F${row}`, time.to);
  });
};

const setWorkTimes = (sheet, branches = []) => {
  if (!branches.length) return;

  const gapRowsBetweenTables = 1;
  const workTimeBlockStep = WORK_TIME_TEMPLATE_HEIGHT + gapRowsBetweenTables;

  branches.forEach((branch, index) => {
    const startRow = WORK_TIME_TEMPLATE_START + index * workTimeBlockStep;

    setWorkTimeBlock(sheet, branch, startRow, index);
  });
};

const setBranchData = (sheet, branch, startRow, index) => {
  setCell(
 sheet,
 `A${startRow}`,
 branch?.name || ""
);

  setCell(sheet, `B${startRow + 1}`, branch?.location || "");
  setCell(sheet, `B${startRow + 2}`, branch?.coordination || "");
  setCell(sheet, `B${startRow + 3}`, branch?.phone || "");
  setCell(sheet, `B${startRow + 4}`, "");
};

const createExtraBranchBlock = (sheet, startRow) => {
  sheet.insertRows(startRow, [[], [], [], [], []]);

  // ننسخ تصميم Branch 2 الموجود في الصفوف 29 إلى 33
  for (let i = 0; i < 5; i++) {
    copyRowStyle(sheet, 29 + i, startRow + i);
  }

  // ننسخ عناوين الخانات من Branch 2
  setCell(sheet, `A${startRow}`, sheet.getCell("A29").value || "Branch");
  setCell(sheet, `A${startRow + 1}`, sheet.getCell("A30").value || "");
  setCell(sheet, `A${startRow + 2}`, sheet.getCell("A31").value || "");
  setCell(sheet, `A${startRow + 3}`, sheet.getCell("A32").value || "");
  setCell(sheet, `A${startRow + 4}`, sheet.getCell("A33").value || "");
};

const setBranches = (sheet, branches = []) => {
  branches.forEach((branch, index) => {
    let startRow;

    if (index === 0) {
      startRow = 24;
    } else if (index === 1) {
      startRow = 29;
    } else {
      startRow = 29 + index * 5;
      createExtraBranchBlock(sheet, startRow);
    }

    setBranchData(sheet, branch, startRow, index);
  });
};


export const validateJoiningExport =
(
 item
)=>{

 const details =
 item?.details || {};

 const branches =
 item?.branches || [];

 if(
 !item?.businessName
 ){

 return {
 valid:false,
 message:
 "اسم المطعم مفقود"
 };

 }

 if(
 !branches.length
 ){

 return {
 valid:false,
 message:
 "الفروع غير مكتملة"
 };

 }

 if(
 !details.ownerName ||
 !details.ownerPhone
 ){

 return {
 valid:false,
 message:
 "بيانات المالك ناقصة"
 };

 }

 return {
 valid:true
 };

};

export const exportJoiningRequirements = async (item) => {
  const response = await fetch(joiningTemplate);
  const arrayBuffer = await response.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const sheet = workbook.worksheets[0];

  const details = item?.details || {};
  const moreDetails = item?.moreDetails || {};
  const branches = item?.branches || [];

  // Public Information
  setCell(sheet, "B2", moreDetails.businessNameEn || "");
  setCell(sheet, "B3", item?.businessName || "");
  setCell(sheet, "B4", item?.branchNumber || branches.length || "");
  setCell(sheet, "B5", item?.city || "");
  setCell(sheet, "B6", details.type || "");
  setCell(sheet, "B7", details.organization || "");
  setCell(sheet, "B8", details.commercialNumber || "");
  setCell(sheet, "B9", details.taxNumber || "");
  setCell(sheet, "B10", "");

  // Marketing Contact
  setCell(sheet, "B11", moreDetails.salesPhone || "");
  setCell(sheet, "B12", moreDetails.salesEmail || "");

  // Accounting Information
  setCell(sheet, "B14", moreDetails.bankName || "");
  setCell(sheet, "B15", moreDetails.bankBeneficiaryName || "");
  setCell(sheet, "B16", moreDetails.IBANnum || "");

  // Owner & Management Information
  setCell(sheet, "B18", details.ownerName || "");
  setCell(sheet, "B19", details.ownerPhone || "");
  setCell(sheet, "B20", details.ownerEmail || "");

  setCell(sheet, "B21", item?.name || "");
  setCell(sheet, "B22", item?.phone || "");
  setCell(sheet, "B23", item?.email || "");

  // Branches
  setBranches(sheet, branches);

  // Work Time لكل الفروع
  setWorkTimes(sheet, branches);

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${item?.businessName || "restaurant"}_joining_requirements.xlsx`
  );
};
